import {
  createFileRoute,
  isRedirect,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { getDefaultStore } from 'jotai';
import {
  accountDetailsOptions,
  featuresOptions,
  projectsOptions,
  organizationsOptions,
  activeMemberRoleOptions,
  sessionOptions,
} from '@/hooks/query-options';
import { setActiveOrganization } from '@/lib/auth-client';
import { activeOrgIdAtom, projectIdAtom } from '@/state/atoms';
import { Loader } from '@/components/ui/loader';

// Guard so the legacy active-org fallback runs its cache-wiping
// setActiveOrganization at most once per org per app load. Without this, a
// session that keeps reporting no activeOrganizationId re-nukes every query
// cache on each beforeLoad run (including hover preloads) — a refetch/redirect
// storm that flashes the full-screen loader until a hard refresh re-reads the
// session with the org the first setActive already wrote to the cookie.
const autoActivatedOrgs = new Set<string>();

export const Route = createFileRoute('/_auth')({
  pendingComponent: () => <Loader fullScreen />,
  beforeLoad: async ({ context, location }) => {
    // Block this route on the session being resolved. ensureQueryData hits
    // the TanStack Query cache after the first load (long staleTime), so
    // subsequent navigations are free. authClient.getSession() itself has no
    // client-side dedup — calling it directly here would re-fire on every
    // navigation. Routes outside _auth (sign-in, sign-up, /demo, /shares) do
    // not pay this cost — they mount immediately.
    let session;
    try {
      session = await context.queryClient.ensureQueryData(sessionOptions());
    } catch (error) {
      if (isRedirect(error)) throw error;
      session = null;
    }

    if (!session) {
      throw redirect({
        to: '/sign-in',
        search: {
          redirect: location.href,
        },
      });
    }

    // Platform-level role from the user record (better-auth admin plugin).
    // Computed up-front so it survives any catch-block fallback below.
    const platformRole =
      (session?.user as { role?: string } | undefined)?.role ?? 'user';

    // Ensure an active organization is set in the session. New sessions get
    // this server-side via databaseHooks.session.create.before in
    // packages/auth/src/auth.ts. The branch below is a one-time fallback for
    // legacy sessions issued before that hook landed; once it runs, the
    // session has activeOrganizationId and subsequent navigations skip it.
    let activeOrganizationId = session?.session?.activeOrganizationId ?? null;
    let hasActiveOrg = !!activeOrganizationId;

    if (session && !hasActiveOrg) {
      try {
        const orgs = await context.queryClient.ensureQueryData(
          organizationsOptions({ userId: session.user.id }),
        );
        const firstOrganizationId = orgs[0]?.id;

        if (firstOrganizationId && !autoActivatedOrgs.has(firstOrganizationId)) {
          const result = await setActiveOrganization(
            firstOrganizationId,
            context.queryClient,
          );
          if (!result.error) {
            // Record only on success so a failed switch retries on the next
            // load rather than being adopted as done. The helper removed
            // ['auth', 'session'] and triggered Better Auth's $sessionSignal so
            // useSession() consumers refetch; locally we just need the new id
            // for the rest of this loader (the cookie is in place downstream).
            autoActivatedOrgs.add(firstOrganizationId);
            activeOrganizationId = firstOrganizationId;
            hasActiveOrg = true;
          }
        } else if (firstOrganizationId) {
          // Already successfully auto-activated this org earlier in this app
          // session: the cookie is set, so adopt it locally without re-running
          // the cache-wiping switch. Re-running would invalidate the session
          // and remove every org-scoped query on each beforeLoad, storming the
          // UI (the full-screen-loader flicker).
          getDefaultStore().set(activeOrgIdAtom, firstOrganizationId);
          activeOrganizationId = firstOrganizationId;
          hasActiveOrg = true;
        }
      } catch (error) {
        if (isRedirect(error)) throw error;
        context.projectId = null;
        context.setProjectId(null);
        return {
          userRole: 'limited' as string,
          hasActiveOrg: false,
          platformRole,
        };
      }
    }

    // Resolve the user's role in the active organization
    let userRole: string = 'limited';
    if (hasActiveOrg && session) {
      try {
        userRole = await context.queryClient.ensureQueryData(
          activeMemberRoleOptions({
            userId: session.user.id,
            activeOrganizationId: activeOrganizationId!,
          }),
        );
      } catch (error) {
        if (isRedirect(error)) throw error;
        // Proceed with default 'limited' role — components retry via React Query
      }
    }

    // Users without an org (brand-new sign-up, or invitation flow before
    // accepting) need to be sent to onboarding — otherwise the dashboard
    // renders and child components fire org-scoped queries with a null
    // projectId, which the backend rejects with 500. Skip the redirect when
    // the user is already on onboarding or accept-invitation, which both
    // handle the no-org case themselves.
    if (!hasActiveOrg) {
      // Anonymous demo visitor with no demo org (not provisioned): /demo drops
      // the throwaway session and shows unavailable, never the onboarding wizard.
      const isAnon =
        (session?.user as { isAnonymous?: boolean } | undefined)
          ?.isAnonymous === true;
      if (isAnon) {
        throw redirect({ to: '/demo' });
      }
      const isOnboardingFlow =
        location.pathname.startsWith('/onboarding') ||
        location.pathname.startsWith('/accept-invitation');
      if (!isOnboardingFlow) {
        throw redirect({ to: '/onboarding' });
      }
      context.projectId = null;
      context.setProjectId(null);
      return { userRole, hasActiveOrg, platformRole };
    }

    // Block on account + projects so child routes (incl. _side-layout) read
    // them from cache instead of triggering parallel network calls. If any
    // throw, return what we have — components retry via React Query.
    try {
      await context.queryClient.ensureQueryData(accountDetailsOptions());
      // Plan locks read features synchronously, so they must be in cache before first paint.
      await context.queryClient.ensureQueryData(featuresOptions());
      const projects =
        await context.queryClient.ensureQueryData(projectsOptions());

      // Resolve a valid projectId once at the auth boundary so child route
      // loaders can trust context.projectId during intent preloading. The
      // active org id is mirrored into Jotai so derived projectIdAtom reads
      // the current org's slot from the persisted projectsByOrgAtom map.
      const store = getDefaultStore();
      store.set(activeOrgIdAtom, activeOrganizationId);
      const stored = store.get(projectIdAtom);
      const isValidProject = (id: string | null): id is string =>
        !!id &&
        projects.some(
          (project: { projectId: string }) => project.projectId === id,
        );
      const resolvedProjectId = isValidProject(stored)
        ? stored
        : (projects[0]?.projectId ?? null);
      if (resolvedProjectId !== stored) {
        store.set(projectIdAtom, resolvedProjectId);
      }
      context.projectId = resolvedProjectId;
      context.setProjectId(resolvedProjectId);

      if (!context.projectId) {
        return { userRole, hasActiveOrg, platformRole };
      }

      return { userRole, hasActiveOrg, platformRole };
    } catch (error) {
      if (isRedirect(error)) throw error;
      return { userRole, hasActiveOrg, platformRole };
    }
  },
  component: AuthLayout,
});

function AuthLayout() {
  return <Outlet />;
}
